import { useEffect, useState, type ReactNode } from 'react';
import { ArrowRight, Check, CircleAlert, Minus, MousePointerClick, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';

/**
 * The per-country tab strip: product creation + edit (`CountryPathwayStep`) and the dossier
 * dispatch form (`DossierDispatchForm`). ONE definition so users meet the same control in all
 * three places — the two copies used to be kept in sync by a comment.
 *
 * These tabs scope everything below them to one country, so the switch must be unmissable: a
 * white card groups the options, every country is a filled button, the navy fill says which one
 * is live. `hint`, when given, sits above the card and says how the strip works.
 */
export function CountryTabsList({ hint, children }: { hint?: string; children: ReactNode }) {
  return (
    <>
      {hint && (
        <p className="flex items-center gap-2 text-navy font-semibold text-[13.5px] m-0 mb-2.5">
          <MousePointerClick className="size-4 text-electric shrink-0" aria-hidden />
          {hint}
        </p>
      )}
      {/* White card on the grey page so the strip stands out; each country inside is a filled
          button (the inactive ones used to be bare grey text on a grey track). */}
      <TabsList className="bg-white border border-line-strong shadow-card rounded-2xl p-1.5 mb-5 h-auto group-data-[orientation=horizontal]/tabs:h-auto flex-wrap justify-start gap-1.5">
        {children}
      </TabsList>
    </>
  );
}

/** 'partial' = a multi-market country (UK) with some sub-markets classified but not all. */
export type CountryTabStatus = 'done' | 'partial' | 'none';

const STATUS = {
  done: { title: 'Ready', word: 'ready', Icon: Check },
  // Generic on purpose: "partly done" means a UK sub-market still unclassified on the product
  // pages, and documents still missing on the dossier.
  partial: { title: 'Partly done', word: 'partly done', Icon: Minus },
  none: { title: 'Incomplete', word: 'incomplete', Icon: X },
} as const;

export function CountryTab({
  value,
  label,
  status,
  testId,
}: {
  value: string;
  label: string;
  status: CountryTabStatus;
  testId: string;
}) {
  const { title, word, Icon } = STATUS[status];
  return (
    <TabsTrigger
      value={value}
      data-testid={testId}
      className={cn(
        `group cursor-pointer relative flex-none
         inline-flex items-center gap-2.5 px-5 py-2.5 rounded-xl
         font-semibold text-[13px] border
         focus-visible:outline-2 focus-visible:outline-electric focus-visible:outline-offset-2
         data-[state=active]:font-bold data-[state=active]:shadow-card
         transition-all`,
        // The selected country is always solid navy; the others are green once finished, soft
        // amber when partly done, grey until then.
        `data-[state=active]:bg-navy! data-[state=active]:text-white! data-[state=active]:border-navy!`,
        status === 'done' &&
          'bg-state-success-bg text-state-success-text border-state-success-border hover:border-state-success-accent',
        status === 'partial' &&
          'bg-state-warning-bg-soft text-state-warning-text border-state-warning-border hover:border-state-warning-accent',
        status === 'none' && 'bg-line-soft text-navy border-line hover:bg-white hover:border-electric',
      )}
    >
      <span>{label}</span>
      {/* Status mark — green check = done, amber dash = partly done, amber cross = not started
          (amber, not red: an unfinished country is a step to do, not an error). A filled circle
          keeps it legible on light and solid tabs alike; the word stays for screen readers. */}
      <span
        title={title}
        className={cn(
          'inline-flex size-5 items-center justify-center rounded-full border',
          // Done = SOLID dark green with a white check: the tab itself is light green, so a light
          // green mark would vanish into it. On the navy selected tab the dark green would sink
          // into the navy — bright green there.
          status === 'done' &&
            `bg-state-success-text text-white border-state-success-text
             group-data-[state=active]:bg-state-success-accent group-data-[state=active]:border-state-success-accent`,
          status === 'partial' &&
            'bg-state-warning-bg-strong text-state-warning-text-strong border-state-warning-border-strong',
          status === 'none' && 'bg-state-warning-bg text-state-warning-text border-state-warning-border',
        )}
      >
        <Icon className="size-3.5" strokeWidth={3} aria-hidden />
        <span className="sr-only">{word}</span>
      </span>
    </TabsTrigger>
  );
}

/**
 * "Next: <country>" under a country's tab body: walks the user on to the next country, so the
 * other tabs are found even by someone who never notices the strip. It only moves on when
 * `problem` is null; otherwise pressing it keeps the user here and shows `problem` beside it
 * (a disabled button would not say why). The message clears once the problem is fixed.
 */
export function NextCountryButton({
  nextLabel,
  problem,
  onNext,
}: {
  nextLabel: string;
  problem: string | null;
  onNext: () => void;
}) {
  const [tried, setTried] = useState(false);
  useEffect(() => {
    if (!problem) setTried(false);
  }, [problem]);
  return (
    <div className="mt-4 flex flex-wrap items-center gap-3">
      <Button
        type="button"
        onClick={() => {
          if (problem) {
            setTried(true);
            return;
          }
          onNext();
        }}
      >
        Next: {nextLabel} <ArrowRight className="size-3.5" aria-hidden />
      </Button>
      {tried && problem && (
        <p role="alert" className="m-0 flex items-center gap-1.5 text-[13px] font-medium text-error">
          <CircleAlert className="size-4 shrink-0" aria-hidden />
          {problem}
        </p>
      )}
    </div>
  );
}
